/**
 * 收起 rail(64px)时钟按钮:状态色块 + 角点 + 下方常驻时长(只到分),
 * hover 立即弹出 FlyoutCard、离开 260ms 延迟收起、点击固定开关、Esc 关闭。
 * 像素/结构真相源:handoff variants.jsx RailVariant(140-187 行),类名 nvc- 前缀。
 */
import { useEffect, useRef, useState } from "react";
import { BriefcaseBusiness, Clock, Coffee } from "lucide-react";
import "./clock-panel.css";
import { formatHm } from "./derive";
import { FlyoutCard, type FlyoutCardProps } from "./FlyoutCard";

export type RailClockButtonProps = Omit<FlyoutCardProps, "onClose" | "className">;

export function RailClockButton(props: RailClockButtonProps) {
  const { displayMode, tick } = props;

  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);

  // hover 进=立即开(清关闭计时);出=260ms 后关(RailVariant 144-145 行)
  const openNow = () => {
    if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 260);
  };
  useEffect(
    () => () => {
      if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
    },
    [],
  );

  // Esc 关闭(仅 open 时挂监听,core.jsx useEsc 同语义)
  useEffect(() => {
    if (!open) return undefined;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]);

  // 任务中用强调色区分于工作中(绿)与休息(琥珀);idle/ended=灰(RailVariant 150 行)
  const railCls =
    displayMode === "activity"
      ? "accent"
      : displayMode === "working"
        ? "green"
        : displayMode === "break"
          ? "amber"
          : "gray";
  // 图标随状态:休息=咖啡杯,任务=公文包,其余=时钟(RailVariant 152 行)
  const RailIcon =
    displayMode === "break" ? Coffee : displayMode === "activity" ? BriefcaseBusiness : Clock;
  // 下方常驻时长:只到分,不含秒(RailVariant 154-156 行)
  const railTime =
    displayMode === "idle"
      ? "0h 0m"
      : displayMode === "break"
        ? formatHm(tick.curBreakSec)
        : formatHm(tick.workSec);

  return (
    <div
      className="nvc-rel"
      style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
    >
      <button className={"nvc-railbtn st-" + displayMode} onClick={() => setOpen((o) => !o)}>
        <RailIcon size={17} />
        <span className={"nvc-cornerdot " + railCls} />
      </button>
      <span className={"nvc-rail-time " + railCls}>{railTime}</span>
      {open && <FlyoutCard {...props} className="rail" onClose={() => setOpen(false)} />}
    </div>
  );
}
